import React from 'react';
import {Box} from '@mui/material';
import {useSearchParams} from 'react-router';
import DocumentContextMenus from '../../../../../components/DocumentContextMenus';
import useFragmentMarks from '../../../../../components/evidenceModal/useFragmentMarks';
import useSourceFileSelectionMenu from './useSourceFileSelectionMenu';

function SourceFileDraftSelectionHostInner({
    draftContext,
    children,
    sx = null,
}) {
    const contentRef = React.useRef(null);
    const [searchParams] = useSearchParams();
    const fragmentMarks = useFragmentMarks();

    const {
        menuPosition,
        citeAnchorEl,
        setCiteAnchorEl,
        handleContextMenu,
        handleMouseDown,
        handleMenuClose,
        handleCiteToFragment,
        handleDragStartSelection,
        onAddToSourceRoot,
        onAddToSourceFolder,
        onAddToSourceNewFolder,
        onAddToAnotherProduct,
    } = useSourceFileSelectionMenu({
        reportId: draftContext.reportId,
        referenceId: draftContext.referenceId,
        docTitle: draftContext.docTitle,
        docUrl: draftContext.docUrl,
        segmentsByBlockIndex: draftContext.segmentsByBlockIndex,
        toCanonicalBlockIndex: draftContext.toCanonicalBlockIndex,
        contentRef,
    });

    const selectedElementName = searchParams.get('elementName');
    const selectedElementId = searchParams.get('elementId');
    const selectedFragmentUid = selectedElementName === 'report-fragment' ? selectedElementId : null;
    const selectedFragmentMark = React.useMemo(() => {
        if (!selectedFragmentUid) return null;
        return (fragmentMarks || []).find((fragment) => fragment.uid === selectedFragmentUid) || null;
    }, [fragmentMarks, selectedFragmentUid]);

    return (
        <>
            <Box
                ref={contentRef}
                onContextMenu={handleContextMenu}
                onMouseDown={handleMouseDown}
                onDragStart={handleDragStartSelection}
                className="evidence-allow-select"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    ...sx,
                }}
            >
                {children}
            </Box>

            <DocumentContextMenus
                menuPosition={menuPosition}
                onClose={handleMenuClose}
                citeAnchorEl={citeAnchorEl}
                setCiteAnchorEl={setCiteAnchorEl}
                fragmentMarks={fragmentMarks}
                selectedFragmentUid={selectedFragmentUid}
                selectedFragmentMark={selectedFragmentMark}
                onCiteToFragment={handleCiteToFragment}
                onAddToSourceRoot={onAddToSourceRoot}
                onAddToSourceFolder={onAddToSourceFolder}
                onAddToAnotherProduct={onAddToAnotherProduct}
            />
        </>
    );
}

/**
 * Wraps selectable source-file text and shows the Corpus-style context menu
 * ("Cite for", "Add to source package") when opened from a FINTEL draft.
 */
const SourceFileDraftSelectionHost = ({
    draftContext = null,
    children,
    sx = null,
}) => {
    const enabled = Boolean(
        draftContext?.reportId
        && draftContext?.referenceId,
    );

    if (!enabled) {
        if (sx) {
            return (
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    ...sx,
                }}>
                    {children}
                </Box>
            );
        }
        return children;
    }

    return (
        <SourceFileDraftSelectionHostInner draftContext={draftContext} sx={sx}>
            {children}
        </SourceFileDraftSelectionHostInner>
    );
};

export default SourceFileDraftSelectionHost;
